import { guardGameSurface } from './186-game-surface.mjs';
/** Independent held steering and bounce controls. Canvas supports immediate
 * legacy taps only when selected; default drags never secretly trigger a jump.
 * Every captured pointer and held key is released on cancellation or pause. */
export function createBobInput({
  ctx,
  stage,
  root,
  listen,
  playable,
  tap,
  steer,
  getCanvas,
  getMode,
  getProjection,
}) {
  const controls = ctx.e('div', 'bob-controls');
  const left = ctx.e('button', 'bob-steer', '←');
  const right = ctx.e('button', 'bob-steer', '→');
  const bounce = ctx.e('button', 'bob-bounce-action', 'Bounce');
  for (const [button, label] of [
    [left, 'Steer left'],
    [right, 'Steer right'],
    [bounce, 'Bounce'],
  ]) {
    button.type = 'button';
    button.setAttribute('aria-label', label);
  }
  controls.append(left, right, bounce);
  stage.append(controls);
  const held = new Map(),
    keys = new Set();
  let drag = null,
    bounceHeld = false;
  function direction() {
    const values = [...held.values()].map((v) => v.direction);
    steer(
      Math.max(
        -1,
        Math.min(
          1,
          values.reduce((a, b) => a + b, 0) +
            (keys.has('ArrowLeft') ? 1 : 0) -
            (keys.has('ArrowRight') ? 1 : 0),
        ),
      ),
    );
  }
  function release(event) {
    const active = held.get(event.pointerId);
    held.delete(event.pointerId);
    if (active?.node.hasPointerCapture?.(event.pointerId))
      active.node.releasePointerCapture(event.pointerId);
    direction();
  }
  for (const [node, value] of [
    [left, 1],
    [right, -1],
  ]) {
    listen(node, 'pointerdown', (e) => {
      if (!playable() || e.button !== 0) return;
      e.preventDefault();
      held.set(e.pointerId, { node, direction: value });
      node.setPointerCapture?.(e.pointerId);
      direction();
    });
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'])
      listen(node, name, release);
    listen(node, 'keydown', (e) => {
      if (['Space', 'Enter'].includes(e.code) && playable()) {
        e.preventDefault();
        held.set(node, { node, direction: value });
        direction();
      }
    });
    listen(node, 'keyup', (e) => {
      if (['Space', 'Enter'].includes(e.code)) {
        held.delete(node);
        direction();
      }
    });
    listen(node, 'blur', () => {
      held.delete(node);
      direction();
    });
  }
  listen(bounce, 'pointerdown', (e) => {
    if (playable() && e.button === 0) {
      e.preventDefault();
      tap();
    }
  });
  listen(bounce, 'click', (e) => {
    if (e.detail === 0) tap();
  });
  listen(root, 'keydown', (e) => {
    if (![root, getCanvas()].includes(e.target) || !playable()) return;
    if (['ArrowLeft', 'ArrowRight'].includes(e.key)) {
      e.preventDefault();
      keys.add(e.key);
      direction();
    } else if (['Space', 'Enter'].includes(e.code)) {
      e.preventDefault();
      if (!e.repeat && !bounceHeld) tap();
      bounceHeld = true;
    }
  });
  listen(root, 'keyup', (e) => {
    if (keys.delete(e.key)) {
      e.preventDefault();
      direction();
    }
    if (['Space', 'Enter'].includes(e.code)) {
      if (bounceHeld) e.preventDefault();
      bounceHeld = false;
    }
  });
  function bind(canvas) {
    listen(canvas, 'pointerdown', (e) => {
      if (!playable() || drag || e.button !== 0 || e.isPrimary === false) return;
      e.preventDefault();
      canvas.focus?.({ preventScroll: true });
      drag = { id: e.pointerId, node: canvas, x: e.clientX };
      canvas.setPointerCapture?.(e.pointerId);
      if (getMode() === 'classic') {
        const rect = canvas.getBoundingClientRect();
        const projected = getProjection?.();
        const centre = projected?.x ?? rect.left + rect.width / 2;
        const offset =
          (e.clientX - centre) / (Math.max(48, rect.width * 0.16, projected?.radius || 0) * 2);
        tap(Math.abs(offset) < 0.18 ? 0 : -offset);
      }
    });
    listen(canvas, 'pointermove', (e) => {
      if (!playable() || !drag || e.pointerId !== drag.id) return;
      e.preventDefault();
      steer(-(e.clientX - drag.x) / Math.max(64, canvas.getBoundingClientRect().width * 0.25));
    });
    const end = (e) => {
      if (!drag || e.pointerId !== drag.id) return;
      const previous = drag;
      drag = null;
      steer(0);
      if (previous.node.hasPointerCapture?.(previous.id))
        previous.node.releasePointerCapture(previous.id);
    };
    for (const name of ['pointerup', 'pointercancel', 'lostpointercapture'])
      listen(canvas, name, end);
    listen(canvas, 'click', (e) => {
      if (e.detail === 0 && !bounceHeld) tap();
    });
  }
  function clear() {
    const hadSteering = held.size || keys.size || drag;
    const captured = [...held.entries()];
    held.clear();
    keys.clear();
    bounceHeld = false;
    for (const [id, value] of captured)
      if (value.node.hasPointerCapture?.(id)) value.node.releasePointerCapture(id);
    const old = drag;
    drag = null;
    if (old?.node.hasPointerCapture?.(old.id)) old.node.releasePointerCapture(old.id);
    if (hadSteering) steer(0);
  }
  guardGameSurface(root, { listen, defaults: false, cancel: clear, ownsTouch: () => playable() });
  return {
    bind,
    clear,
    paint() {
      for (const button of [left, right, bounce]) button.disabled = !playable();
    },
  };
}
